import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface BacklinkFormProps {
  onSuccess: () => void;
  userId: string;
}

export const BacklinkForm = ({ onSuccess, userId }: BacklinkFormProps) => {
  const [formData, setFormData] = useState({
    website_name: '',
    website_url: '',
    domain_authority: '',
    category: '',
    contact_email: '',
    contact_name: '',
    outreach_status: 'not_contacted',
    backlink_type: '',
    notes: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const { error } = await supabase.from('backlinks').insert({
      agent_id: userId,
      website_name: formData.website_name,
      website_url: formData.website_url,
      domain_authority: formData.domain_authority ? parseInt(formData.domain_authority) : null,
      category: formData.category,
      contact_email: formData.contact_email || null,
      contact_name: formData.contact_name || null,
      outreach_status: formData.outreach_status,
      backlink_type: formData.backlink_type || null,
      notes: formData.notes || null,
    });

    setLoading(false);

    if (error) {
      toast.error('Failed to add backlink');
      console.error(error);
    } else {
      toast.success('Backlink added successfully');
      setFormData({
        website_name: '',
        website_url: '',
        domain_authority: '',
        category: '',
        contact_email: '',
        contact_name: '',
        outreach_status: 'not_contacted',
        backlink_type: '',
        notes: '',
      });
      onSuccess();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="website_name">Website Name *</Label>
          <Input
            id="website_name"
            required
            value={formData.website_name}
            onChange={(e) => setFormData({ ...formData, website_name: e.target.value })}
            placeholder="e.g., Home Design Magazine"
          />
        </div>
        <div>
          <Label htmlFor="website_url">Website URL *</Label>
          <Input
            id="website_url"
            type="url"
            required
            value={formData.website_url}
            onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
            placeholder="https://example.com"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="category">Category *</Label>
          <Select
            value={formData.category}
            onValueChange={(value) => setFormData({ ...formData, category: value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Home Improvement">Home Improvement</SelectItem>
              <SelectItem value="Bathroom Design">Bathroom Design</SelectItem>
              <SelectItem value="Smart Home Technology">Smart Home Technology</SelectItem>
              <SelectItem value="European Publications">European Publications</SelectItem>
              <SelectItem value="Business Directory">Business Directory</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="domain_authority">Domain Authority (DA)</Label>
          <Input
            id="domain_authority"
            type="number"
            min="0"
            max="100"
            value={formData.domain_authority}
            onChange={(e) => setFormData({ ...formData, domain_authority: e.target.value })}
            placeholder="0-100"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="contact_name">Contact Name</Label>
          <Input
            id="contact_name"
            value={formData.contact_name}
            onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
            placeholder="John Doe"
          />
        </div>
        <div>
          <Label htmlFor="contact_email">Contact Email</Label>
          <Input
            id="contact_email"
            type="email"
            value={formData.contact_email}
            onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
            placeholder="contact@example.com"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="outreach_status">Outreach Status</Label>
          <Select
            value={formData.outreach_status}
            onValueChange={(value) => setFormData({ ...formData, outreach_status: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="not_contacted">Not Contacted</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="secured">Secured</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="backlink_type">Backlink Type</Label>
          <Input
            id="backlink_type"
            value={formData.backlink_type}
            onChange={(e) => setFormData({ ...formData, backlink_type: e.target.value })}
            placeholder="Guest Post, Directory, Resource Page"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          rows={4}
          placeholder="Additional notes about this backlink opportunity..."
        />
      </div>

      <Button type="submit" disabled={loading} className="w-full">
        {loading ? 'Adding...' : 'Add Backlink'}
      </Button>
    </form>
  );
};
